import { extent, group, max, sum, timeDay, timeMonth, timeWeek, timeYear } from 'd3';
import { useMemo } from 'react';
import { element, useElementProps } from '../../../core/elements';
import { scaleBand, scaleBandTime, scaleLinear, scaleOrdinal } from '../../../primitives/scale';
import getBandwidth from '../../../primitives/shape/utils/getBandwidth';
import { Theme } from '../../../primitives/theme';
export function useMaximumYValue(data, x, y, stacked) {
  const total = useMemo(() => {
    if (!stacked) return max(data, y) || 1;
    const grouped = group(data, x);
    const totals = [];
    grouped.forEach(colorGroup => {
      totals.push(sum(colorGroup.map(y)));
    });
    return max(totals) || 1;
  }, [data, x, y, stacked]);
  return total;
}

/** Public props */

export function calculateXScaleDomain(_ref) {
  let {
    xData,
    timeUnit,
    range,
    padding
  } = _ref;
  if (timeUnit) {
    // @ts-expect-error unknown error expected
    const timeExtent = extent(xData);
    let timeInterval = timeYear;
    switch (timeUnit) {
      case 'days':
        timeInterval = timeDay;
        break;
      case 'months':
        timeInterval = timeMonth;
        break;
      case 'weeks':
        timeInterval = timeWeek;
        break;
      default:
        break;
    }
    const timeDomain = timeInterval.range(timeExtent[0], timeInterval.offset(timeExtent[1], 1), 1);
    return scaleBandTime({
      range,
      padding,
      domain: timeDomain
    });
  } else {
    // @ts-expect-error unknown error expected
    return scaleBand({
      domain: [...new Set(xData)],
      range,
      padding
    });
  }
}
export function calculateYDomain(_ref2) {
  let {
    totalYValue,
    yDomain,
    normalize
  } = _ref2;
  const domainOutput = normalize ? [0, 1] : [0, totalYValue];
  if (yDomain && yDomain[0] != null) {
    domainOutput[0] = yDomain[0];
  }
  if (yDomain && yDomain[1] != null) {
    domainOutput[1] = yDomain[1];
  }
  return domainOutput;
}

// TODO: Split this up between each bar type in a better way.
export const BarScalesElement = element(props => {
  const {
    data,
    x,
    y,
    color,
    width,
    height,
    yDomain,
    timeUnit
  } = props;
  const {
    fillStyles,
    enableDefaultPatterns,
    useThemeFillStyles
  } = useElementProps(Theme);
  const totalYValue = useMaximumYValue(data || [], x, y, false);
  const value = useMemo(() => {
    const xScaleRange = [0, width];
    const xScalePadding = 0.25;
    const xScale = calculateXScaleDomain({
      xData: data.map(x),
      timeUnit,
      range: xScaleRange,
      padding: xScalePadding
    });
    const groupScale = scaleBand({
      domain: [...new Set(data.map(color))],
      range: [0, getBandwidth(xScale)],
      padding: 0.1
    });
    const yScale = scaleLinear({
      domain: calculateYDomain({
        totalYValue,
        yDomain
      }),
      range: [height, 0]
    });
    const {
      domain,
      range
    } = useThemeFillStyles({
      fillStyles,
      enableDefaultPatterns
    });
    const colorScale = scaleOrdinal({
      domain: domain ? domain : data.map(color),
      range: range
    });

    // This needs to go somewhere else. And should probably use the x data.
    const seriesData = group(data, x);
    return {
      xScale,
      yScale,
      colorScale,
      groupScale,
      seriesData
    };
  }, [data, width, height, totalYValue, x, yDomain, timeUnit, fillStyles, enableDefaultPatterns]);
  return props.children(value);
});